/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { EmailAttachment, REPORT_JOB_COLOR_SELECTION, ReportService } from '@gh/builder-shared';
import {
  ContactRestService,
  ContactType,
  CostCenterRestService,
  CsMasterRestService,
  JobColorSelectionRestService,
  reportIdField,
} from '@gh/builder-shared-data';
import { getRouteParam, getRouteQueryParam, InitViewService } from '@gh/common-shared';
import { AutocompleteItem, ID, withDisplayAsNameAndDescription } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { pick } from 'lodash';
import { Observable } from 'rxjs';
import {
  JobCsConsultant,
  jobCsConsultantFromContactListItemDto,
  JobCsDetails,
  JobCsDetailsCc,
  jobCsDetailsFromDto,
  JobCsDetailsHeading,
  JobCsDetailsItem,
  JobCsDetailsSubheading,
  jobCsDetailsToDto,
  jobCsHeadingClone,
  jobCsHeadingGenerateId,
  jobCsItemGenerateId,
  jobCsSubheadingClone,
  jobCsSubheadingGenerateId,
} from './job-cs-details.model';

@Injectable()
export class JobCsDetailsBusinessService implements InitViewService {

  constructor(private translations: TranslationService,
              private jobColorSelectionRest: JobColorSelectionRestService,
              private colorSelectionMasterRest: CsMasterRestService,
              private contactRest: ContactRestService,
              private costCenterRest: CostCenterRestService,
              private report: ReportService,
              private gridLayouts: GridLayoutService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    const jobId = getRouteParam(route, 'jobId');
    const csMasterId = getRouteQueryParam(route, 'csMasterId');

    return {
      headingGridLayout: this.gridLayouts.get('jobCsHeadings'),
      subheadingGridLayout: this.gridLayouts.get('jobCsSubheadings'),
      itemGridLayout: this.gridLayouts.get('jobCsItems'),
      costCenterGridLayout: this.gridLayouts.get('jobCsCostCenters'),
      qcGridLayout: this.gridLayouts.get('jobCsQuoteChanges'),
      reportUrl: this.report.generateUrl(REPORT_JOB_COLOR_SELECTION, [reportIdField('Job', jobId)]),
      init: this.getColorSelection(jobId, csMasterId),
    };
  }

  createNewHeading(): JobCsDetailsHeading {
    return {
      id: jobCsHeadingGenerateId(),
      csHeadingId: '',
      name: this.translations.get('lbl_new_heading'),
      isDirty: true,
      isNew: true,
      isHidden: false,
      isNewPage: false,
      isOqc: false,
      subHeadings: [],
    };
  }

  cloneHeading(heading: JobCsDetailsHeading): JobCsDetailsHeading {
    return {
      ...this.createNewHeading(),
      ...pick(jobCsHeadingClone(heading), ['isHidden', 'isNewPage', 'subHeadings']),
    };
  }

  createNewSubheading(): JobCsDetailsSubheading {
    return {
      id: jobCsSubheadingGenerateId(),
      csSubHeadingId: '',
      name: this.translations.get('lbl_new_sub_heading'),
      isDirty: true,
      isNew: true,
      isHidden: false,
      isOqc: false,
      memo: '',
      items: [],
    };
  }

  cloneSubheading(subheading: JobCsDetailsSubheading): JobCsDetailsSubheading {
    return {
      ...this.createNewSubheading(),
      ...pick(jobCsSubheadingClone(subheading), ['isHidden', 'memo', 'items']),
    };
  }

  createNewItem(costCenters: JobCsDetailsCc[]): JobCsDetailsItem {
    return {
      id: jobCsItemGenerateId(),
      csItemId: '',
      name: this.translations.get('lbl_new_item'),
      description: '',
      isComponent: false,
      isVariationRequired: false,
      isDirty: true,
      isNew: true,
      isQuoteChange: false,
      costCenters,
      isHidden: false,
    };
  }

  findEmployeesByName(query: FilteredDataQuery<string>): Observable<JobCsConsultant[]> {
    return this.contactRest.findAllAsDataSource({
      name: query.filter,
      autocomplete: true,
      types: [ContactType.Employee],
      showInactive: false,
    }).select(query).pipe(map$(getSlicedData), map$((contacts) => contacts.map(jobCsConsultantFromContactListItemDto)));
  }

  findCostCentersByName(query: FilteredDataQuery<string>, excludeCcIds: ID[]): Observable<AutocompleteItem[]> {
    return this.costCenterRest
      .findAllAutocompleteItemsAsDataSource({
        searchString: query.filter,
        showDeleted: false,
        excludeIds: excludeCcIds,
      }).select(query).pipe(
        map$(getSlicedData),
        map$((items) => items.map(withDisplayAsNameAndDescription)));
  }

  getColorSelection(jobId: ID, csMasterId?: ID): Observable<JobCsDetails> {
    return this.jobColorSelectionRest.get(jobId, csMasterId).pipe(map$((dto) => jobCsDetailsFromDto(jobId, dto)));
  }

  updateColorSelection(jobId: ID, details: JobCsDetails): Observable<JobCsDetails> {
    return this.jobColorSelectionRest.update(jobId, jobCsDetailsToDto(details)).pipe(
      map$((dto) => jobCsDetailsFromDto(jobId, dto)));
  }

  findCsMastersByName(query: FilteredDataQuery<any>): Observable<AutocompleteItem[]> {
    return this.colorSelectionMasterRest.findAllAsFinderDataSource({
        showDeleted: false,
        searchString: query.filter,
      })
      .select(query).pipe(map$(getSlicedData));
  }

  produce(jobId: ID): Observable<EmailAttachment> {
    return this.jobColorSelectionRest.produce(jobId);
  }
}
